<script setup>
import { computed } from 'vue';
import { useMessageContext } from '../../provider.js';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { MESSAGE_VARIANTS } from '../../constants';

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
});

const { variant, contentAttributes } = useMessageContext();
const typebotToken = 'CW_TYPEBOT_BLANK';
const typebotPlaceholder = '__CW_TYPEBOT_BLANK__';
const isTypebot = computed(() => !!contentAttributes.value?.typebot);

const shouldDisableEmphasis = computed(() => {
  const raw = props.content || '';
  const compact = raw.replace(/\s+/g, '');

  if (compact.length < 30) {
    return false;
  }

  if (!/[\\*_]/.test(compact)) {
    return false;
  }

  const digits = (compact.match(/[0-9]/g) || []).length;
  const digitRatio = digits / compact.length;

  // Treat number-heavy payload strings (PIX/QR payloads) as plain text
  return digitRatio > 0.35;
});

/**
 * Clean up orphaned markdown markers from Typebot messages
 * Typebot sometimes sends malformed markdown like "**text:** value**"
 * This function removes orphaned ** and __ that don't form valid pairs
 */
const cleanTypebotMarkdown = text => {
  let cleaned = text;

  // Convert **bold** pairs first to protect them
  cleaned = cleaned.replace(/\*\*(.+?)\*\*/g, '⟪$1⟫');
  // Remove remaining ** (orphans)
  cleaned = cleaned.replace(/\*\*/g, '');
  // Restore bold markers
  cleaned = cleaned.replace(/⟪(.+?)⟫/g, '**$1**');

  // Same for underline
  cleaned = cleaned.replace(/__(.+?)__/g, '⟪$1⟫');
  cleaned = cleaned.replace(/__/g, '');
  cleaned = cleaned.replace(/⟪(.+?)⟫/g, '__$1__');

  return cleaned;
};

// Ensure readable text on dark agent/bot bubbles in light mode
// and force black text for deleted message notice in light mode
const proseColorClasses = computed(() => {
  const isDeleted =
    contentAttributes.value?.deleted ||
    (typeof props.content === 'string' &&
      props.content.trim().startsWith('⛔'));
  const isAgentOrBot =
    variant.value === MESSAGE_VARIANTS.AGENT ||
    variant.value === MESSAGE_VARIANTS.BOT;
  const isTemplate = variant.value === MESSAGE_VARIANTS.TEMPLATE;
  if (isDeleted) {
    return [
      'text-black',
      'prose-headings:text-black',
      'prose-strong:text-black',
      'prose-a:text-black',
      'prose-code:text-black',
      'prose-th:text-black',
      'prose-code:bg-n-alpha-3',
      'dark:text-n-slate-12',
      'dark:prose-headings:text-n-slate-12',
      'dark:prose-strong:text-n-slate-12',
      'dark:prose-a:text-n-slate-12',
      'dark:prose-code:text-n-slate-12',
      'dark:prose-code:bg-n-alpha-3',
      'dark:prose-th:text-n-slate-12',
    ];
  }
  if (isAgentOrBot || isTemplate) {
    return [
      'text-white',
      'prose-headings:text-white',
      'prose-strong:text-white',
      'prose-a:text-white',
      'prose-code:text-white',
      'prose-code:bg-white/10',
      'prose-pre:text-white',
      'prose-pre:bg-white/10',
      'prose-th:text-white',
      'dark:prose-headings:text-white',
      'dark:prose-strong:text-white',
      'dark:prose-a:text-white',
      'dark:prose-code:text-white',
      'dark:prose-code:bg-white/10',
      'dark:prose-pre:text-white',
      'dark:prose-pre:bg-white/10',
      'dark:prose-th:text-white',
    ];
  }
  return [];
});

const formattedContent = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) {
    return props.content;
  }

  // Clean Typebot markdown before formatting
  const content = isTypebot.value
    ? cleanTypebotMarkdown(props.content || '')
    : props.content || '';

  return new MessageFormatter(
    content,
    false,
    false,
    true,
    false,
    shouldDisableEmphasis.value
  ).formattedMessage;
});

const typebotContent = computed(() => {
  const raw = (props.content || '').replace(/\r\n/g, '\n');
  const hasToken = raw.includes(typebotToken);
  if (!isTypebot.value && !hasToken) return null;

  const sanitized = raw
    .replace(/\r/g, '\n')
    .replace(new RegExp(typebotToken, 'g'), '\n')
    .replace(new RegExp(typebotPlaceholder, 'g'), '\n');

  // Clean orphaned markdown markers
  const cleaned = cleanTypebotMarkdown(sanitized);

  return new MessageFormatter(
    cleaned,
    false,
    false,
    true,
    false,
    shouldDisableEmphasis.value
  ).formattedMessage;
});
</script>

<template>
  <span
    v-dompurify-html="typebotContent || formattedContent"
    class="prose prose-bubble"
    :class="[proseColorClasses]"
  />
</template>
